import {
  inlineTextSpanProperties,
  parseInlineTextSpanTag,
} from "./inlineTextSpan";

/** Minimal mdast node shape this transform reads and rewrites. */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: { hName?: string; hProperties?: Record<string, string> };
}

/** The rendered element one open tag stands for. */
interface OpenTag {
  /** The tag the run renders as. */
  name: string;
  /** Extra properties for the rendered element, or null when it needs none. */
  properties: Record<string, string> | null;
}

/**
 * The inline HTML tags markdown bodies carry formatting as. Markdown has no
 * underline syntax at all, and its emphasis delimiters cannot sit flush
 * between text and a punctuation-edged run (CommonMark flanking), so those
 * runs are stored as bare tags instead.
 */
const INLINE_MARK_TAGS = new Set(["u", "b", "i"]);

/**
 * remark plugin rendering the inline HTML markdown stores formatting as when
 * its own syntax cannot express it: the bare `<u>`/`<b>`/`<i>` tags, and the
 * `<span size="..." color="...">` a per-run size or colour is stored as. remark
 * parses the tags as bare `html` nodes, which the renderer would otherwise
 * drop. An unclosed tag is left alone, so it renders as nothing rather than
 * formatting the rest of the block.
 * @returns The remark transformer
 */
export function remarkInlineMarks() {
  return (tree: MdNode): void => rewrite(tree);
}

/**
 * Recursively wraps every tag-delimited run in its own formatting node.
 * @param node - The mdast node whose children are rewritten in place
 */
function rewrite(node: MdNode): void {
  if (!node.children) {
    return;
  }
  node.children.forEach(rewrite);
  node.children = wrapRuns(node.children);
}

/**
 * Replaces each open/close tag pair in one child list with a formatting node
 * holding the nodes between them, recursing into the wrapped run so nested
 * pairs (an underline around a bold, a span inside a span) wrap too.
 * @param children - The node's children, already rewritten depth-first
 * @returns The children with formatted runs wrapped
 */
function wrapRuns(children: MdNode[]): MdNode[] {
  const open = children.findIndex((child) => openTagOf(child) !== null);
  if (open === -1) {
    return children;
  }
  const tag = openTagOf(children[open]);
  if (!tag) {
    return children;
  }
  const close = matchingCloseIndex(children, open, tag.name);
  if (close === -1) {
    return [
      ...children.slice(0, open + 1),
      ...wrapRuns(children.slice(open + 1)),
    ];
  }
  return [
    ...children.slice(0, open),
    {
      type: "inlineMark",
      data: markData(tag),
      children: wrapRuns(children.slice(open + 1, close)),
    },
    ...wrapRuns(children.slice(close + 1)),
  ];
}

/**
 * The index of the close tag ending a run, skipping past the pairs of the same
 * tag nested inside it.
 * @param children - The node's children
 * @param open - Index of the run's open tag
 * @param name - The tag name being closed
 * @returns The close tag's index, or -1 when the run is never closed
 */
function matchingCloseIndex(
  children: MdNode[],
  open: number,
  name: string,
): number {
  const closeTag = `</${name}>`;
  const found = children.slice(open + 1).reduce<{
    depth: number;
    at: number | null;
  }>(
    (state, child, index) => {
      if (state.at !== null) {
        return state;
      }
      if (isTag(child, closeTag)) {
        return state.depth === 0
          ? { depth: 0, at: index }
          : { depth: state.depth - 1, at: null };
      }
      return openTagOf(child)?.name === name
        ? { depth: state.depth + 1, at: null }
        : state;
    },
    { depth: 0, at: null },
  );
  return found.at === null ? -1 : found.at + open + 1;
}

/**
 * The mdast `data` telling the renderer which element a run becomes.
 * @param tag - The open tag the run was delimited by
 * @returns The renderer hints for the wrapping node
 */
function markData(tag: OpenTag): MdNode["data"] {
  return tag.properties === null
    ? { hName: tag.name }
    : { hName: tag.name, hProperties: tag.properties };
}

/**
 * The element a node opens, or null when it is not a formatting open tag.
 * @param node - The candidate mdast node
 * @returns The open tag, or null
 */
function openTagOf(node: MdNode): OpenTag | null {
  if (node.type !== "html") {
    return null;
  }
  const raw = node.value?.trim() ?? "";
  const bare = /^<([a-z]+)>$/.exec(raw);
  if (bare) {
    return INLINE_MARK_TAGS.has(bare[1])
      ? { name: bare[1], properties: null }
      : null;
  }
  const span = parseInlineTextSpanTag(raw);
  return span === null
    ? null
    : { name: "span", properties: inlineTextSpanProperties(span) };
}

/**
 * Whether a node is the given bare inline HTML tag.
 * @param node - The candidate mdast node
 * @param tag - The tag to match
 * @returns True when the node is exactly that tag
 */
function isTag(node: MdNode, tag: string): boolean {
  return node.type === "html" && node.value?.trim() === tag;
}
